/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */


/**
 * PWA Service for BuyerView.ai
 *
 * Handles Progressive Web App functionality including:
 * - Service Worker registration and updates
 * - App installation prompts
 * - Offline detection and handling
 * - Push notification management
 * - Background sync for offline actions
 */

export interface PWAInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface OfflineAction {
  id: string;
  type: 'review' | 'image' | 'user_action';
  data: any;
  timestamp: number;
  retryCount: number;
}

export interface PWAState {
  isInstalled: boolean;
  isOffline: boolean;
  canInstall: boolean;
  updateAvailable: boolean;
  swRegistration: ServiceWorkerRegistration | null;
  installPrompt: PWAInstallPromptEvent | null;
}

class PWAService {
  private state: PWAState = {
    isInstalled: false,
    isOffline: false,
    canInstall: false,
    updateAvailable: false,
    swRegistration: null,
    installPrompt: null
  };

  private listeners: Map<string, Set<(state: PWAState) => void>> = new Map();
  private offlineActions: OfflineAction[] = [];
  private dbName = 'buyerview-offline';
  private dbVersion = 1;

  /**
   * Initialize PWA service
   */
  async initialize(): Promise<void> {
    try {
      // Check if running as PWA
      this.checkInstallationStatus();

      // Register service worker
      await this.registerServiceWorker();

      // Set up offline detection
      this.setupOfflineDetection();

      // Set up install prompt handling
      this.setupInstallPrompt();

      // Set up background sync
      this.setupBackgroundSync();

      // Initialize IndexedDB for offline storage
      await this.initializeOfflineStorage();

      console.log('✅ PWA Service initialized');
    } catch (error) {
      console.error('❌ PWA Service initialization failed:', error);
    }
  }

  /**
   * Check if app is installed as PWA
   */
  private checkInstallationStatus(): void {
    // Check display mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;

    // Check if launched from home screen
    const isFromHomeScreen = (window.navigator as any).standalone === true;

    this.state.isInstalled = isStandalone || isFullscreen || isMinimalUI || isFromHomeScreen;

    // Listen for display mode changes
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      this.state.isInstalled = e.matches;
      this.notifyListeners('installation');
    });
  }

  /**
   * Register service worker.
   *
   * DISABLED 2026-05-29: A stale SW from an earlier deploy pinned users
   * to a cached index.html referencing missing /assets/*.js bundles,
   * triggering main.tsx's "Loading Error" fallback. /public/sw.js has
   * been replaced with a self-destruct script that unregisters itself
   * and clears all buyerview-* caches on next visit. We do NOT register
   * any new SW until we have:
   *   1. A real asset-hash-aware caching strategy
   *   2. A reliable update flow (skipWaiting + clientsClaim + prompt)
   *   3. Actual users who benefit from offline support
   * Pre-launch there is nothing to gain and a lot to lose from a SW.
   *
   * The body below now actively unregisters any leftover SW the user
   * may still have so we don't depend on /sw.js firing — belt and braces.
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));

      if ('caches' in self) {
        const keys = await caches.keys();
        await Promise.all(
          keys
            .filter((k) => k.startsWith('buyerview-'))
            .map((k) => caches.delete(k).catch(() => false))
        );
      }
    } catch (_error) {
      // Best effort — never throw from PWA init.
    }
  }

  /**
   * Set up offline detection
   */
  private setupOfflineDetection(): void {
    this.state.isOffline = !navigator.onLine;

    const updateOnlineStatus = () => {
      const wasOffline = this.state.isOffline;
      this.state.isOffline = !navigator.onLine;

      if (wasOffline && !this.state.isOffline) {
        // Back online - trigger sync
        this.syncOfflineActions();
      }

      this.notifyListeners('online');
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
  }

  /**
   * Set up install prompt handling
   */
  private setupInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.state.installPrompt = e as PWAInstallPromptEvent;
      this.state.canInstall = true;
      this.notifyListeners('install');
    });

    window.addEventListener('appinstalled', () => {
      this.state.isInstalled = true;
      this.state.canInstall = false;
      this.state.installPrompt = null;
      this.notifyListeners('installation');
    });
  }

  /**
   * Set up background sync
   */
  private setupBackgroundSync(): void {
    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SYNC_COMPLETE') {
          this.handleSyncComplete(event.data);
        }
      });
    }
  }

  /**
   * Initialize offline storage (IndexedDB)
   */
  private async initializeOfflineStorage(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('offlineActions')) {
          const store = db.createObjectStore('offlineActions', { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('cachedData')) {
          db.createObjectStore('cachedData', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Show install prompt
   */
  async showInstallPrompt(): Promise<{ outcome: 'accepted' | 'dismissed' }> {
    if (!this.state.installPrompt) {
      throw new Error('Install prompt not available');
    }

    await this.state.installPrompt.prompt();
    const result = await this.state.installPrompt.userChoice;

    if (result.outcome === 'accepted') {
      this.state.canInstall = false;
      this.state.installPrompt = null;
    }

    this.notifyListeners('install');
    return result;
  }

  /**
   * Update service worker
   */
  async updateServiceWorker(): Promise<void> {
    if (!this.state.swRegistration) {
      throw new Error('No service worker registration');
    }

    const newWorker = this.state.swRegistration.waiting;
    if (newWorker) {
      newWorker.postMessage({ type: 'SKIP_WAITING' });

      // Reload page after activation
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  }

  /**
   * Store offline action
   */
  async storeOfflineAction(action: Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
    const offlineAction: OfflineAction = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      ...action
    };

    try {
      await this.saveToIndexedDB('offlineActions', offlineAction);
      this.offlineActions.push(offlineAction);

      // Register for background sync
      if (this.state.swRegistration && 'sync' in this.state.swRegistration) {
        await this.state.swRegistration.sync.register(`${action.type}-sync`);
      }

      return offlineAction.id;
    } catch (error) {
      console.error('Failed to store offline action:', error);
      throw error;
    }
  }

  /**
   * Sync offline actions when back online
   */
  private async syncOfflineActions(): Promise<void> {
    if (this.state.isOffline) {
      return;
    }

    const actions = await this.getFromIndexedDB('offlineActions');

    for (const action of actions) {
      try {
        await this.processOfflineAction(action);
        await this.removeFromIndexedDB('offlineActions', action.id);
      } catch (error) {
        console.error('Failed to sync offline action:', error);

        // Increment retry count
        action.retryCount++;
        if (action.retryCount < 3) {
          await this.saveToIndexedDB('offlineActions', action);
        } else {
          // Remove after 3 failed attempts
          await this.removeFromIndexedDB('offlineActions', action.id);
        }
      }
    }
  }

  /**
   * Process individual offline action
   */
  private async processOfflineAction(action: OfflineAction): Promise<void> {
    switch (action.type) {
      case 'review':
        await this.syncReviewAction(action);
        break;
      case 'image':
        await this.syncImageAction(action);
        break;
      case 'user_action':
        await this.syncUserAction(action);
        break;
      default:
        console.warn('Unknown offline action type:', action.type);
    }
  }

  /**
   * Sync review action
   */
  private async syncReviewAction(action: OfflineAction): Promise<void> {
    const response = await fetch('/api/reviews', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(action.data)
    });

    if (!response.ok) {
      throw new Error(`Review sync failed: ${response.statusText}`);
    }
  }

  /**
   * Sync image action
   */
  private async syncImageAction(action: OfflineAction): Promise<void> {
    const formData = new FormData();
    formData.append('image', action.data.blob);
    formData.append('metadata', JSON.stringify(action.data.metadata));

    const response = await fetch('/api/images', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Image sync failed: ${response.statusText}`);
    }
  }

  /**
   * Sync user action
   */
  private async syncUserAction(action: OfflineAction): Promise<void> {
    const response = await fetch(action.data.url, {
      method: action.data.method || 'POST',
      headers: action.data.headers || { 'Content-Type': 'application/json' },
      body: action.data.body
    });

    if (!response.ok) {
      throw new Error(`User action sync failed: ${response.statusText}`);
    }
  }

  /**
   * Handle sync completion from service worker
   */
  private handleSyncComplete(data: any): void {
    console.log('Background sync completed:', data);
    this.notifyListeners('sync');
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission(): Promise<'granted' | 'denied' | 'default'> {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPushNotifications(vapidKey: string): Promise<PushSubscription> {
    if (!this.state.swRegistration) {
      throw new Error('Service worker not registered');
    }

    const subscription = await this.state.swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlB64ToUint8Array(vapidKey)
    });

    return subscription;
  }

  /**
   * Convert VAPID key
   */
  private urlB64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  /**
   * IndexedDB helpers
   */
  private async saveToIndexedDB(storeName: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);

        const saveRequest = store.put(data);
        saveRequest.onsuccess = () => resolve();
        saveRequest.onerror = () => reject(saveRequest.error);
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async getFromIndexedDB(storeName: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);

        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => resolve(getAllRequest.result);
        getAllRequest.onerror = () => reject(getAllRequest.error);
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async removeFromIndexedDB(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);

        const deleteRequest = store.delete(key);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Event listeners
   */
  addEventListener(event: string, callback: (state: PWAState) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  removeEventListener(event: string, callback: (state: PWAState) => void): void {
    this.listeners.get(event)?.delete(callback);
  }

  private notifyListeners(event: string): void {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(this.state);
      } catch (error) {
        console.error('Error in PWA event listener:', error);
      }
    });
  }

  /**
   * Get current PWA state
   */
  getState(): PWAState {
    return { ...this.state };
  }
}

export const pwaService = new PWAService();